<div class="form-page">
  <div class="container-fluid">
    <!-- Page Header -->
    <div class="row mb-4">
      <div class="col">
        <button class="btn btn-link text-muted px-0 mb-2" (click)="goBack()">
          <i class="bi bi-arrow-left me-1"></i>
          Back to Chemicals
        </button>
        <h1 class="h2 mb-1 fw-bold">{{ isEditMode ? 'Edit Chemical' : 'Create Chemical' }}</h1>
      </div>
    </div>

    <!-- Error/Success Messages -->
    <div *ngIf="errorMessage" class="alert alert-danger alert-dismissible" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ errorMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="errorMessage = ''"></button>
    </div>
    <div *ngIf="successMessage" class="alert alert-success alert-dismissible" role="alert">
      <i class="bi bi-check-circle-fill me-2"></i>
      {{ successMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="successMessage = ''"></button>
    </div>

    <!-- Loading State -->
    <div *ngIf="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Form -->
    <form *ngIf="!loading" [formGroup]="form" (ngSubmit)="save()">
      <div class="row g-4">
        <!-- Left Column: Basic Information -->
        <div class="col-lg-6">
          <!-- Required Fields Card -->
          <div class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0"><i class="bi bi-info-circle me-2"></i>Basic Information</h5>
            </div>
            <div class="card-body">
              <div class="mb-3">
                <label class="form-label">Name <span class="text-danger">*</span></label>
                <input
                  type="text"
                  class="form-control"
                  formControlName="name"
                  placeholder="e.g., Roundup PowerMax"
                  [class.is-invalid]="form.get('name')?.invalid && form.get('name')?.touched"
                />
                <div class="invalid-feedback">Name is required</div>
              </div>

              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Category <span class="text-danger">*</span></label>
                  <select
                    class="form-select"
                    formControlName="category"
                    [class.is-invalid]="form.get('category')?.invalid && form.get('category')?.touched"
                  >
                    <option value="">Select category</option>
                    <option *ngFor="let cat of categoryOptions" [value]="cat.value">{{ cat.label }}</option>
                  </select>
                  <div class="invalid-feedback">Category is required</div>
                </div>

                <div class="col-md-6 mb-3">
                  <label class="form-label">Signal Word</label>
                  <select
                    class="form-select"
                    formControlName="signalWord"
                  >
                    <option value="">Select signal word</option>
                    <option *ngFor="let sw of signalWordOptions" [value]="sw.value">{{ sw.label }}</option>
                  </select>
                  <small class="form-text text-muted">May be populated from EPA data</small>
                </div>
              </div>
            </div>
          </div>

          <!-- Optional Fields Card -->
          <div class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0"><i class="bi bi-list-ul me-2"></i>Additional Details</h5>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">EPA Number</label>
                  <input
                    type="text"
                    class="form-control"
                    formControlName="epaNumber"
                    placeholder="e.g., 524-537, 91234-32, or 91234-205"
                    [class.is-invalid]="epaNumberError"
                  />
                  <div class="invalid-feedback">{{ epaNumberError }}</div>
                  <small class="form-text text-muted">EPA registration number</small>
                </div>

                <div class="col-md-6 mb-3">
                  <label class="form-label">Manufacturer</label>
                  <input
                    type="text"
                    class="form-control"
                    formControlName="manufacturer"
                    placeholder="e.g., Bayer"
                  />
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">Active Ingredient</label>
                <input
                  type="text"
                  class="form-control"
                  formControlName="activeIngredient"
                  placeholder="e.g., Glyphosate"
                />
              </div>

              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Formulation</label>
                  <input
                    type="text"
                    class="form-control"
                    formControlName="formulation"
                    placeholder="e.g., Liquid concentrate"
                  />
                </div>

                <div class="col-md-6 mb-3">
                  <label class="form-label">Default Rate</label>
                  <input
                    type="text"
                    class="form-control"
                    formControlName="defaultRate"
                    placeholder="e.g., 2 oz/acre"
                  />
                </div>
              </div>

              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">REI (Restricted Entry Interval)</label>
                  <input
                    type="text"
                    class="form-control"
                    formControlName="rei"
                    placeholder="e.g., 4 hours"
                  />
                </div>

                <div class="col-md-6 mb-3">
                  <label class="form-label">PHI (Pre-Harvest Interval)</label>
                  <input
                    type="text"
                    class="form-control"
                    formControlName="phi"
                    placeholder="e.g., 7 days"
                  />
                </div>
              </div>

              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">SDS URL</label>
                  <input
                    type="url"
                    class="form-control"
                    formControlName="sdsUrl"
                    placeholder="https://example.com/sds.pdf"
                  />
                  <small class="form-text text-muted">Safety Data Sheet link</small>
                </div>

                <div class="col-md-6 mb-3">
                  <label class="form-label">Product URL</label>
                  <input
                    type="url"
                    class="form-control"
                    formControlName="productUrl"
                    placeholder="https://sponsor.com/product"
                  />
                  <small class="form-text text-muted">Direct link to product on sponsor's site</small>
                </div>
              </div>

              <!-- Target Pests Tag Input -->
              <div class="mb-3">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <label class="form-label mb-0">Target Pests</label>
                  <button
                    *ngIf="targetPestsTags.length > 0"
                    type="button"
                    class="btn btn-link btn-sm text-muted p-0"
                    (click)="clearAllPests()"
                  >
                    Clear all
                  </button>
                </div>
                <div class="border rounded p-2">
                  <div class="tags-display d-flex flex-wrap gap-2 mb-2 p-1 overflow-y-auto" *ngIf="targetPestsTags.length > 0">
                    <span
                      *ngFor="let pest of targetPestsTags; let i = index"
                      class="badge rounded-pill text-bg-primary d-inline-flex align-items-center gap-1 text-wrap text-break text-start mw-100"
                    >
                      {{ pest }}
                      <button type="button" class="btn-close btn-close-white" (click)="removePestTag(i)"></button>
                    </span>
                  </div>
                  <div class="d-flex gap-2">
                    <input
                      type="text"
                      class="form-control border-0 bg-transparent shadow-none py-1 px-2"
                      [(ngModel)]="newPestInput"
                      [ngModelOptions]="{standalone: true}"
                      placeholder="Type and press Enter to add..."
                      (keydown)="onPestInputKeydown($event)"
                    />
                    <button
                      type="button"
                      class="btn btn-outline-primary flex-shrink-0 py-1 px-2"
                      (click)="addPestTag()"
                      [disabled]="!newPestInput.trim()"
                    >
                      <i class="bi bi-plus-lg"></i>
                    </button>
                  </div>
                </div>
                <small class="form-text text-muted">{{ targetPestsTags.length }} pest(s) added</small>
              </div>

              <!-- Crops Tag Input -->
              <div class="mb-3">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <label class="form-label mb-0">Crops</label>
                  <button
                    *ngIf="cropsTags.length > 0"
                    type="button"
                    class="btn btn-link btn-sm text-muted p-0"
                    (click)="clearAllCrops()"
                  >
                    Clear all
                  </button>
                </div>
                <div class="border rounded p-2">
                  <div class="tags-display d-flex flex-wrap gap-2 mb-2 p-1 overflow-y-auto" *ngIf="cropsTags.length > 0">
                    <span
                      *ngFor="let crop of cropsTags; let i = index"
                      class="badge rounded-pill text-bg-primary d-inline-flex align-items-center gap-1 text-wrap text-break text-start mw-100"
                    >
                      {{ crop }}
                      <button type="button" class="btn-close btn-close-white" (click)="removeCropTag(i)"></button>
                    </span>
                  </div>
                  <div class="d-flex gap-2">
                    <input
                      type="text"
                      class="form-control border-0 bg-transparent shadow-none py-1 px-2"
                      [(ngModel)]="newCropInput"
                      [ngModelOptions]="{standalone: true}"
                      placeholder="Type and press Enter to add..."
                      (keydown)="onCropInputKeydown($event)"
                    />
                    <button
                      type="button"
                      class="btn btn-outline-primary flex-shrink-0 py-1 px-2"
                      (click)="addCropTag()"
                      [disabled]="!newCropInput.trim()"
                    >
                      <i class="bi bi-plus-lg"></i>
                    </button>
                  </div>
                </div>
                <small class="form-text text-muted">{{ cropsTags.length }} crop(s) added</small>
              </div>

              <div class="mb-3">
                <label class="form-label">Compare To</label>
                <input
                  type="text"
                  class="form-control"
                  formControlName="compareTo"
                  placeholder="e.g., Quadris Top"
                />
                <small class="form-text text-muted">Equivalent brand name product</small>
              </div>

              <div class="mb-3">
                <div class="form-check form-switch">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    id="rupFlag"
                    formControlName="rupFlag"
                  />
                  <label class="form-check-label" for="rupFlag">
                    <strong>Restricted Use Pesticide (RUP)</strong>
                    <small class="d-block text-muted">Product requires certified applicator</small>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column: Sponsor, Featured & Labels -->
        <div class="col-lg-6">
          <!-- Sponsor Section Card (Moved to top) -->
          <div class="card mb-4">
            <div class="card-header sponsor-header cursor-pointer user-select-none" (click)="toggleSponsorSection()">
              <div class="d-flex justify-content-between align-items-center">
                <h5 class="mb-0">
                  <i class="bi bi-building me-2"></i>
                  Sponsor/Partner Information
                </h5>
                <i class="bi" [class.bi-chevron-down]="!sponsorSectionExpanded" [class.bi-chevron-up]="sponsorSectionExpanded"></i>
              </div>
            </div>
            <div class="card-body" *ngIf="sponsorSectionExpanded">
              <!-- Sponsor Selection -->
              <div class="mb-3">
                <label class="form-label">Select Sponsor</label>
                <div class="d-flex gap-2">
                  <select
                    class="form-select"
                    formControlName="sponsorId"
                    (change)="onSponsorChange()"
                  >
                    <option value="">No sponsor</option>
                    <option *ngFor="let sponsor of sponsors" [value]="sponsor.id">{{ sponsor.name }}</option>
                  </select>
                  <button type="button" class="btn btn-outline-primary flex-shrink-0" (click)="openNewSponsorModal()">
                    <i class="bi bi-plus-lg me-1"></i>
                    New
                  </button>
                </div>
                <small *ngIf="loadingSponsors" class="form-text text-muted">Loading sponsors...</small>
              </div>

              <!-- Selected Sponsor Details (Read-only) -->
              <div *ngIf="selectedSponsor" class="sponsor-details mt-3 p-3 rounded" style="background-color: var(--bg-tertiary);">
                <div class="d-flex justify-content-between align-items-start mb-2">
                  <h6 class="mb-0">{{ selectedSponsor.name }}</h6>
                  <button type="button" class="btn btn-sm btn-outline-secondary" (click)="clearSponsor()">
                    <i class="bi bi-x-lg"></i>
                  </button>
                </div>
                <div *ngIf="selectedSponsor.logoUrl" class="mb-2">
                  <img [src]="getLogoFullUrl(selectedSponsor.logoUrl)" alt="Sponsor logo" class="img-thumbnail" style="max-height: 60px;" />
                </div>
                <div *ngIf="selectedSponsor.website" class="small text-muted">
                  <i class="bi bi-globe me-1"></i>
                  <a [href]="selectedSponsor.website" target="_blank">{{ selectedSponsor.website }}</a>
                </div>
                <div *ngIf="selectedSponsor.phone" class="small text-muted">
                  <i class="bi bi-telephone me-1"></i>{{ selectedSponsor.phone | phoneFormat }}
                </div>
                <div *ngIf="selectedSponsor.email" class="small text-muted">
                  <i class="bi bi-envelope me-1"></i>{{ selectedSponsor.email }}
                </div>
              </div>
            </div>
          </div>

          <!-- Featured & Special Offer Card -->
          <div class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0"><i class="bi bi-star me-2"></i>Featured & Promotions</h5>
            </div>
            <div class="card-body">
              <div class="mb-3">
                <div class="form-check form-switch">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    id="isFeatured"
                    formControlName="isFeatured"
                  />
                  <label class="form-check-label" for="isFeatured">
                    <strong>Featured Chemical</strong>
                    <small class="d-block text-muted">Display prominently in chemical lists</small>
                  </label>
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">Special Offer</label>
                <input
                  type="text"
                  class="form-control"
                  formControlName="specialOffer"
                  placeholder="e.g., 10% off for ADN members"
                />
              </div>
            </div>
          </div>

          <!-- Labels Section (Edit Mode Only) -->
          <div *ngIf="isEditMode" class="card mb-4">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h5 class="mb-0"><i class="bi bi-file-earmark-pdf me-2"></i>Labels ({{ labels.length }})</h5>
              <button
                type="button"
                class="btn btn-sm btn-outline-primary"
                (click)="toggleAddLabelForm()"
              >
                <i class="bi" [class.bi-plus]="!showAddLabelForm" [class.bi-x]="showAddLabelForm"></i>
                {{ showAddLabelForm ? 'Cancel' : 'Add Label' }}
              </button>
            </div>
            <div class="card-body" *ngIf="showAddLabelForm">
              <div class="p-3 rounded mb-3" style="background-color: var(--bg-tertiary, #f8f9fa);">
                <div class="mb-2">
                  <label class="form-label small">Label URL <span class="text-danger">*</span></label>
                  <input
                    type="url"
                    class="form-control form-control-sm"
                    [(ngModel)]="newLabelUrl"
                    [ngModelOptions]="{standalone: true}"
                    placeholder="https://example.com/label.pdf"
                    (keydown.enter)="$event.preventDefault()"
                  />
                </div>
                <div class="mb-2">
                  <label class="form-label small">Label Name</label>
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    [(ngModel)]="newLabelName"
                    [ngModelOptions]="{standalone: true}"
                    placeholder="e.g., Product Label v2.1"
                    (keydown.enter)="$event.preventDefault()"
                  />
                </div>
                <div class="mb-2">
                  <label class="form-label small">Accepted Date</label>
                  <input
                    type="date"
                    class="form-control form-control-sm"
                    [(ngModel)]="newLabelDate"
                    [ngModelOptions]="{standalone: true}"
                    (keydown.enter)="$event.preventDefault()"
                  />
                </div>
                <div class="mb-3">
                  <div class="form-check">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      id="setLabelCurrentChem"
                      [(ngModel)]="newLabelSetCurrent"
                      [ngModelOptions]="{standalone: true}"
                    />
                    <label class="form-check-label small" for="setLabelCurrentChem">
                      Set as current label
                    </label>
                  </div>
                </div>
                <button
                  type="button"
                  class="btn btn-sm btn-success w-100"
                  (click)="addLabel()"
                  [disabled]="addingLabel || !newLabelUrl.trim()"
                >
                  <span *ngIf="addingLabel" class="spinner-border spinner-border-sm me-1"></span>
                  Add Label
                </button>
              </div>
            </div>
            <div class="card-body" *ngIf="labels.length === 0 && !showAddLabelForm">
              <div class="text-center text-muted py-3">
                <i class="bi bi-file-earmark-pdf d-block mb-2" style="font-size: 2rem;"></i>
                No labels added yet
              </div>
            </div>
            <ul *ngIf="labels.length > 0" class="list-group list-group-flush">
              <li *ngFor="let label of labels" class="list-group-item d-flex justify-content-between align-items-center">
                <div>
                  <div class="fw-medium">
                    {{ label.labelName || 'Label PDF' }}
                    <span *ngIf="label.isCurrent" class="badge bg-success ms-1">Current</span>
                  </div>
                  <small class="text-muted">{{ formatDate(label.acceptedDate) }}</small>
                </div>
                <div class="btn-group btn-group-sm">
                  <a [href]="label.labelUrl" target="_blank" class="btn btn-outline-primary" title="View">
                    <i class="bi bi-box-arrow-up-right"></i>
                  </a>
                  <button
                    type="button"
                    [class]="label.isCurrent ? 'btn btn-success' : 'btn btn-outline-success'"
                    [title]="label.isCurrent ? 'Unmark as current' : 'Set as current'"
                    (click)="setCurrentLabel(label)"
                    [disabled]="settingCurrentLabelId === label.id"
                  >
                    <i class="bi" [class.bi-check-circle-fill]="label.isCurrent" [class.bi-check-circle]="!label.isCurrent"></i>
                  </button>
                  <button
                    type="button"
                    class="btn btn-outline-danger"
                    title="Delete"
                    (click)="deleteLabel(label)"
                    [disabled]="deletingLabelId === label.id"
                  >
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </li>
            </ul>
          </div>

          <!-- Archive Section (Edit Mode Only) -->
          <div *ngIf="isEditMode" class="card border-danger">
            <div class="card-header bg-danger text-white">
              <h5 class="mb-0"><i class="bi bi-archive me-2"></i>Danger Zone</h5>
            </div>
            <div class="card-body">
              <p class="text-muted mb-3">
                Archiving this chemical will remove it from active lists. This action can be reversed by an administrator.
              </p>
              <button type="button" class="btn btn-outline-danger" (click)="openArchiveModal()">
                <i class="bi bi-archive me-2"></i>
                Archive Chemical
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Form Actions -->
      <div class="row mt-4">
        <div class="col">
          <div class="card">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <span *ngIf="uploadingLogo" class="text-info">
                    <span class="spinner-border spinner-border-sm me-1"></span>
                    Uploading logo...
                  </span>
                </div>
                <div class="d-flex gap-2">
                  <button type="button" class="btn btn-outline-secondary" (click)="goBack()">
                    Cancel
                  </button>
                  <button type="submit" class="btn btn-primary" [disabled]="!canSave">
                    <span *ngIf="saving" class="spinner-border spinner-border-sm me-1"></span>
                    {{ isEditMode ? 'Update Chemical' : 'Create Chemical' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</div>

<!-- Archive Confirmation Modal -->
<div class="modal fade" id="archiveModal" tabindex="-1" *ngIf="showArchiveModal">
  <div class="modal-dialog">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">
          <i class="bi bi-exclamation-triangle text-danger me-2"></i>
          Archive Chemical
        </h5>
        <button type="button" class="btn-close" aria-label="Close" data-bs-dismiss="modal" (click)="cancelArchive()"></button>
      </div>
      <div class="modal-body">
        <p>Are you sure you want to archive this chemical?</p>
        <div class="card">
          <div class="card-body py-2">
            <strong>{{ form.get('name')?.value }}</strong>
          </div>
        </div>
        <div class="alert alert-warning mt-3 mb-0">
          <i class="bi bi-info-circle me-2"></i>
          This chemical will no longer appear in active chemical lists. This action can be reversed by an administrator.
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" (click)="cancelArchive()">
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-danger"
          (click)="executeArchive()"
          [disabled]="archiving"
        >
          <span *ngIf="archiving" class="spinner-border spinner-border-sm me-1"></span>
          Archive Chemical
        </button>
      </div>
    </div>
  </div>
</div>

<!-- New Sponsor Modal -->
<div class="modal fade" id="newSponsorModal" tabindex="-1" *ngIf="showNewSponsorModal">
  <div class="modal-dialog">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">
          <i class="bi bi-building-add me-2"></i>
          Add New Sponsor
        </h5>
        <button type="button" class="btn-close" aria-label="Close" (click)="closeNewSponsorModal()"></button>
      </div>
      <div class="modal-body">
        <div *ngIf="newSponsorError" class="alert alert-danger alert-dismissible" role="alert">
          <i class="bi bi-exclamation-triangle-fill me-2"></i>
          {{ newSponsorError }}
          <button type="button" class="btn-close" aria-label="Close" (click)="newSponsorError = ''"></button>
        </div>

        <div class="mb-3">
          <label class="form-label">Sponsor Name <span class="text-danger">*</span></label>
          <input
            type="text"
            class="form-control"
            [(ngModel)]="newSponsorName"
            [ngModelOptions]="{standalone: true}"
            placeholder="e.g., Acme Ag Products"
          />
        </div>

        <div class="mb-3">
          <label class="form-label">Website</label>
          <input
            type="url"
            class="form-control"
            [(ngModel)]="newSponsorWebsite"
            [ngModelOptions]="{standalone: true}"
            placeholder="e.g., https://www.example.com"
          />
        </div>

        <div class="row">
          <div class="col-md-6 mb-3">
            <label class="form-label">Phone</label>
            <input
              type="tel"
              class="form-control"
              [(ngModel)]="newSponsorPhone"
              [ngModelOptions]="{standalone: true}"
              mask="(000) 000-0000"
              [validation]="false"
              placeholder="e.g., (555) 123-4567"
            />
          </div>

          <div class="col-md-6 mb-3">
            <label class="form-label">Email</label>
            <input
              type="email"
              class="form-control"
              [(ngModel)]="newSponsorEmail"
              [ngModelOptions]="{standalone: true}"
              placeholder="e.g., info@example.com"
            />
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-outline-secondary" (click)="closeNewSponsorModal()">
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary"
          (click)="saveNewSponsor()"
          [disabled]="savingNewSponsor || !newSponsorName.trim()"
        >
          <span *ngIf="savingNewSponsor" class="spinner-border spinner-border-sm me-1"></span>
          Create Sponsor
        </button>
      </div>
    </div>
  </div>
</div>
